import {updateFactory} from '@modifi/redux'
import {aPush} from '@modifi/router'
import {
  aUserFeedBackSubmitSuccessful,
  INTERMEDIARY_SHAREHOLDER_COUNTRY,
} from '@modifi/plugin-onboarding'
import {aShowDialog} from '@modifi/plugin-dialogs'
import {getAuthenticatedThemisClient} from '@modifi/onboarding-api-clients'

import {getLogger, formatAddressData} from '../../../utils'

import {
  mapIntermediaryShareholderCompanyToFormData,
  mapIntermediaryShareholderToRequest,
  pickAllIntermediaryShareHolderProperties,
} from './utils'

import {ROUTE_COMPANY_PROFILE_INTERMEDIARY_SHAREHOLDERS} from '../../../routes/constants'
import {SECTION_INTERMEDIARY_SHAREHOLDERS} from '../../../constants/sections'

import {
  aFetchIntermediaryShareholders,
  selectIntermediaryShareholderByExternalId,
  selectIntermediaryShareholdersWhereLoaded,
} from '../../../data/intermediary-shareholders'
import {aFetchCompany, selectCurrentCompaniesCountryCode} from '../../../data/company'
import {COMPANY_CONFIRMATION_DIALOG} from './dialogs'

import {aFetchInputFields} from '../../../data/input-fields'

const logger = getLogger('intermediary-shareholder-page')

const apiClient = getAuthenticatedThemisClient()

export const selectInitializePagePending = (
  state: Record<string, any>,
  externalIntermediaryShareholderId?: string
): boolean => {
  if (externalIntermediaryShareholderId) {
    const intermediaryShareHoldersWhereLoaded = selectIntermediaryShareholdersWhereLoaded(state)
    if (!intermediaryShareHoldersWhereLoaded) {
      return true
    }
  }
  return false
}

export const selectInitialFormDataForCreation = (
  state: Record<string, any>
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const defaultValues: Record<string, any> = {}

  const initialValues: Record<string, any> = {}

  defaultValues[INTERMEDIARY_SHAREHOLDER_COUNTRY] = selectCurrentCompaniesCountryCode(state)

  Object.assign(initialValues, mapIntermediaryShareholderCompanyToFormData({}))

  return {
    initialValues,
    defaultValues,
  }
}

export const selectInitialFormDataForEditing = (
  state: Record<string, any>,
  externalIntermediaryShareholderId: string
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const defaultValues: Record<string, any> = {}

  const initialValues: Record<string, any> = {}

  const {address, ...rest} =
    selectIntermediaryShareholderByExternalId(state, externalIntermediaryShareholderId) || {}
  Object.assign(
    initialValues,
    mapIntermediaryShareholderCompanyToFormData({
      ...address,
      ...rest,
    })
  )

  return {
    initialValues,
    defaultValues,
  }
}

const {reducer, actions, selectors} = updateFactory({
  rootSelector: state => state?.pages?.intermediaryShareholder,
  actionNamePrefix: 'pages/intermediary-shareholder/form',
  submitRequest:
    (a, changes: Record<string, any>, externalIntermediaryShareholderId?: string | undefined) =>
    async (dispatch): Promise<void> => {
      logger.info('Submitting intermediary shareholder page')
      logger.debug('changes: %o', changes)

      const requestData = mapIntermediaryShareholderToRequest(
        pickAllIntermediaryShareHolderProperties(changes)
      )
      const updatedRequestData = formatAddressData(requestData)

      dispatch(a.pendingAction())

      try {
        logger.info('Create or update the company: %o', updatedRequestData)
        if (externalIntermediaryShareholderId) {
          logger.debug('Intermediary shareholder company already exists, updating...')
          // call update endpoint
          await apiClient.ShareholdingCompanies.update({
            externalShareholdingCompanyId: externalIntermediaryShareholderId,
            body: updatedRequestData,
          })
        } else {
          logger.debug(
            'User input intermediary shareholder company does not exists, trying to create......'
          )
          const body = {
            ...updatedRequestData,
          }
          const saveResponse = await apiClient.ShareholdingCompanies.create({
            body,
            forceCreate: false,
          }).then(res => ({data: res.data(), statusCode: res.status()}))

          if (saveResponse?.statusCode === 302 && saveResponse?.data) {
            dispatch(
              aShowDialog(COMPANY_CONFIRMATION_DIALOG, {
                userInputData: {
                  companyName: updatedRequestData?.companyName,
                  address: updatedRequestData?.address,
                  votingShare: updatedRequestData?.votingShare,
                },
                existingData: {
                  companyName: saveResponse?.data?.companyName,
                  address: saveResponse?.data?.address,
                  externalId: saveResponse?.data?.externalId,
                },
              })
            )
            return
          }
        }
      } catch (error: any) {
        logger.error('Error while creating or updating a person: %o', error)
        dispatch(a.errorAction(error))
      }
      await Promise.all([
        dispatch(aFetchInputFields()),
        dispatch(aFetchCompany()),
        dispatch(aFetchIntermediaryShareholders()),
      ])

      dispatch(aPush(ROUTE_COMPANY_PROFILE_INTERMEDIARY_SHAREHOLDERS))

      dispatch(a.successAction())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_INTERMEDIARY_SHAREHOLDERS))
    },

  reset:
    a =>
    (dispatch): void => {
      dispatch(a.clearAction())
    },
})

export default reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateHasError: selectSaveHasError,
  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors

export const {aReset, aSubmit: aSavePage} = actions
